import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Checkbox,
  FormControlLabel,
  Chip
} from '@mui/material';
import {
  Warning as WarningIcon,
  Assignment as AssignIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

export default function UnplannedEquipmentDialog({
  open,
  onClose,
  equipment,
  job,
  onConfirm,
  onCancel,
  loading = false
}) {
  const [addToPlan, setAddToPlan] = React.useState(true);

  const handleConfirm = () => {
    onConfirm({ addToPlan });
  };

  const handleCancel = () => {
    onCancel();
  };

  if (!equipment || !job) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="warning" />
          <Typography variant="h6" component="span">
            Unplanned Equipment Assignment
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body1" component="div">
            This equipment is not part of the original plan for this job.
          </Typography>
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Equipment Details
          </Typography>
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body1" gutterBottom>
              <strong>Equipment ID:</strong> {equipment.equipment_id || equipment.id}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <strong>Description:</strong> {equipment.description || 'No description'}
            </Typography>
            {equipment.manufacturer && (
              <Typography variant="body1" gutterBottom>
                <strong>Manufacturer:</strong> {equipment.manufacturer}
              </Typography>
            )}
            {equipment.model && (
              <Typography variant="body1" gutterBottom>
                <strong>Model:</strong> {equipment.model}
              </Typography>
            )}
            {equipment.serial_number && (
              <Typography variant="body1" gutterBottom>
                <strong>Serial Number:</strong> {equipment.serial_number}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Job Details
          </Typography>
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body1" gutterBottom>
              <strong>Job:</strong> {job.jobNumber || job.id} - {job.name}
            </Typography>
            {job.client && (
              <Typography variant="body1" gutterBottom>
                <strong>Client:</strong> {job.client}
              </Typography>
            )}
            <Typography variant="body1">
              <strong>Status:</strong> 
              <Chip 
                label={job.status} 
                size="small" 
                sx={{ ml: 1 }} 
                color={job.status === 'Active' ? 'success' : 'default'}
              />
            </Typography>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            ⚠️ <strong>This will add new equipment to the job.</strong>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            The equipment will be automatically added to the job's equipment plan and then assigned.
            This helps maintain visibility of what was originally planned versus what was added during execution.
          </Typography>
        </Alert>

        <FormControlLabel
          control={
            <Checkbox
              checked={addToPlan}
              onChange={(e) => setAddToPlan(e.target.checked)}
              color="primary"
            />
          }
          label="Continue and add to plan"
          sx={{ mb: 1 }}
        />
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          startIcon={<CancelIcon />}
          disabled={loading}
        >
          Cancel Assignment
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          startIcon={<AssignIcon />}
          disabled={loading || !addToPlan}
          color="warning"
        >
          {loading ? 'Processing...' : 'Continue Assignment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}